/**
 * Embed-local loading spinner.
 *
 * Replaces the import of `app/_components/LoadingSpinner.jsx`, which was the only
 * thing pulling `@mui/system` and `@mui/material`'s `Box` into the embed bundle —
 * and which read `theme.palette.page.light` from an MUI `ThemeProvider` the embed
 * never rendered, so its secondary color silently resolved to `undefined`.
 *
 * The outer component keeps a `size` prop for readability at call sites, but passes
 * it down as `diameter`: Emotion forwards any prop that is a valid HTML attribute to
 * the underlying element, and `size` is one (it belongs on `<input>`/`<select>`), so
 * forwarding it would put a bogus `size` attribute on a `<div>`. The other style
 * props are already non-HTML names and so are filtered out automatically.
 */
import React from 'react';
import { keyframes } from '@emotion/react';
import styled from '../theme/styled.js';
import { token } from '../theme/tokens.js';

const spin = keyframes({
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
});

const Spinner = styled.div(({ theme, diameter, thickness, speed, trackColor, arcColor }) => ({
    boxSizing: 'border-box',
    width: diameter,
    height: diameter,
    borderRadius: '50%',
    border: `${thickness}px solid ${trackColor ?? token(theme, 'color.spinnerTrack')}`,
    // Set after the `border` shorthand so it wins; this is the arc that appears to spin.
    borderTopColor: arcColor ?? token(theme, 'color.accent'),
    animation: `${spin} ${speed} linear infinite`,
}));

export const RoundLoadingAnimation = ({
    size = 40,
    thickness = 5,
    speed = '500ms',
    trackColor,
    arcColor,
}) =>
    <Spinner
        // Purely decorative: the panel already exposes its loading state via
        // `aria-busy`, so announcing it here too would just be redundant noise.
        aria-hidden="true"
        diameter={size}
        thickness={thickness}
        speed={speed}
        trackColor={trackColor}
        arcColor={arcColor}
    />
;

const LoadingWrapperContainer = styled.div({
    width: '100%',
    position: 'relative',
});

const LoadingFilter = styled.div(
    {
        width: '100%',
        transition: '150ms all ease-in-out',
    },
    ({ isLoading }) => isLoading ?
        {
            opacity: '50%',
            filter: 'saturate(0%) blur(2px)',
        } : {},
);

const LoadingOverlay = styled.div({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const LoadingWrapper = ({ isLoading, children }) =>
    <LoadingWrapperContainer>
        <LoadingFilter isLoading={isLoading}>
            {children}
        </LoadingFilter>
        {isLoading &&
            <LoadingOverlay>
                <RoundLoadingAnimation />
            </LoadingOverlay>
        }
    </LoadingWrapperContainer>
;

export default LoadingWrapper;
